import { DURATION_BANDS } from "../duration/duration-bands";

/**
 * The Tour Search block's vocabulary: its stored types, its fields and the
 * tours-page query parameter each one submits.
 *
 * THE BLOCK IS A STORED TREE, form included, so an operator styles, reorders
 * and edits it in the canvas like any other block:
 *
 *   tour-search          <form action="/tours" method="get" role="search">
 *   ├── tour-search-fields   the fields box; fields move only inside it
 *   │   ├── tour-search-keyword   <label> — label text, icon, search input
 *   │   └── tc-combobox ×3        one compound picker each (combobox-settings.ts)
 *   │       ├── label, then a row holding the trigger button
 *   │       └── content               the popup: search box (Search only),
 *   │           └── tour-search-options   the list, drawn from data,
 *   │                                     then the status (Search only)
 *   └── tc-submit-button     the Button block, as a submit button
 *
 * The `<form>` is safe to store because document-safety.ts checks every form
 * (form-safety.ts): its `action` must be a path on this site and its `method`
 * GET, and no element may carry `formaction` and its siblings. The block
 * stores `TOUR_SEARCH_ACTION` and `TOUR_SEARCH_METHOD` and offers no trait
 * that changes them.
 *
 * The parameter names must never drift from the tours page. traveler-web's
 * `parseState` (`_filters-shared.ts`) reads the same names from here.
 */
export const TOUR_SEARCH_TYPE = "tour-search";
export const TOUR_SEARCH_FIELDS_TYPE = "tour-search-fields";
export const TOUR_SEARCH_KEYWORD_TYPE = "tour-search-keyword";

/** The tours page. Relative, like the header search's form, so it works on
 *  a custom domain and on the platform subdomain alike. */
export const TOUR_SEARCH_ACTION = "/tours";
export const TOUR_SEARCH_METHOD = "get";

export const TOUR_SEARCH_FIELDS = [
  "keyword",
  "destination",
  "activity",
  "duration",
] as const;
export type TourSearchField = (typeof TOUR_SEARCH_FIELDS)[number];

export const TOUR_SEARCH_PARAMS: Record<TourSearchField, string> = {
  keyword: "q",
  destination: "dest",
  activity: "style",
  duration: "dur",
};

/** The fields drawn as a `<tc-combobox>`. The keyword stays a plain input. */
export const TOUR_SEARCH_PICKERS = [
  "destination",
  "activity",
  "duration",
] as const satisfies readonly TourSearchField[];
export type TourSearchPicker = (typeof TOUR_SEARCH_PICKERS)[number];

export const isTourSearchPicker = (value: unknown): value is TourSearchPicker =>
  TOUR_SEARCH_PICKERS.some((picker) => picker === value);

/** The attribute each field carries, naming which field it is. */
export const TOUR_SEARCH_FIELD_ATTR = "data-field";

export const TOUR_SEARCH_DEFAULT_LABELS: Record<TourSearchField, string> = {
  keyword: "Search",
  destination: "Destination",
  activity: "Activity type",
  duration: "Duration",
};

/** For a picker, the placeholder is also its "any" choice: leaving the picker
 *  on it adds no filter (AC-67). */
export const TOUR_SEARCH_DEFAULT_PLACEHOLDERS: Record<TourSearchField, string> =
  {
    keyword: "Tour name, place or keyword",
    destination: "Anywhere",
    activity: "Any type",
    duration: "Any length",
  };

export const TOUR_SEARCH_DEFAULT_BUTTON_LABEL = "Search tours";

/** Duration's choices: the tours page's own bands, in its own order (AC-66). */
export const durationSearchOptions = (): { value: string; label: string }[] =>
  DURATION_BANDS.map((band) => ({ value: band.id, label: band.label }));
